import { Component, OnInit, AfterViewInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { RouterModule } from '@angular/router';
import { User } from '@angular/fire/auth';
import { forkJoin } from 'rxjs';

import { AuthService } from '../security/auth.service';
import { CustomerService } from '../customer/customer.service';
import { SessionStorageService } from '../services/session-storage.service';
import { ToolbarService } from '../services/toolbar.service';
import { BaseMasterDataService } from '../services/base-master-data.service';
import { MessageService } from 'primeng/api';
import { TransportationAgencyService } from "../company/transportation-agency/transportation-agency.service";
import { EnterpriseFeatureService } from "../services/enterprise-features.service";
import { FreightForwarderService } from "../company/freight-forwarder/freight-forwarder.service";
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { ProgressBarModule } from 'primeng/progressbar';
import { ImageModule } from 'primeng/image';
import { InputTextModule } from 'primeng/inputtext';
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { IftaLabelModule } from "primeng/iftalabel";
import { CheckboxModule } from 'primeng/checkbox';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.css'],
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        RouterModule,
        FontAwesomeModule,
        CardModule,
        ButtonModule,
        ToastModule,
        ProgressBarModule,
        ImageModule,
        InputTextModule,
        InputGroupModule,
        InputGroupAddonModule,
        IftaLabelModule,
        CheckboxModule
    ]
})
export class LoginComponent implements OnInit, AfterViewInit {

    isLoading = false;
    isLoggedIn = false;
    isLoginFailed = false;
    returnUrl: string = '';
    errorMessage = '';
    roles: string[] = [];
    email = '';
    password = '';
    rememberMe = false;

    // Phone Login State
    isPhoneLogin = false;
    phoneNumber = '';
    otp = '';
    confirmationResult: any;

    constructor(private authService: AuthService,
        private sessionStorageService: SessionStorageService,
        private router: Router, private route: ActivatedRoute,
        private customerService: CustomerService,
        private baseMasterDataService: BaseMasterDataService,
        private transportationAgencyService: TransportationAgencyService,
        private freightForwarderService: FreightForwarderService,
        private enterpriseFeatureService: EnterpriseFeatureService,
        private toasterService: MessageService,
        private toolbarService: ToolbarService) {
    }

    ngOnInit(): void {
        this.rememberMe = this.sessionStorageService.isRememberMeEnabled();
        if (this.sessionStorageService.getAccessToken()) {
            this.isLoggedIn = true;
            this.roles = this.sessionStorageService.getUser()?.roles ?? [];
        }
        this.route.queryParams.subscribe(params => {
            this.returnUrl = params['returnUrl'];
            if (this.returnUrl != undefined) {
                this.returnUrl = this.returnUrl.substring(this.findThirdOccurrence(this.returnUrl, '/'), this.returnUrl.length);
            }
            if (params['session'] === 'replaced') {
                this.toasterService.add({
                    severity: 'warn',
                    summary: 'Session replaced',
                    detail: 'This account was opened in another browser window. Sign in again to continue here.'
                });
            }
        });
    }

    ngAfterViewInit(): void {
        if (!this.authService.usesWhatsAppOtp) {
            this.authService.initializeRecaptcha('recaptcha-container');
        }
    }

    loginWithEmail() {
        this.isLoading = true;
        this.authService.login(this.email, this.password, this.rememberMe)
            .subscribe({
                next: (user) => {
                    if (user) {
                        this.loginToBackend(user);
                    }
                },
                error: (error) => {
                    this.isLoading = false;
                    this.toasterService.add({
                        severity: 'error',
                        summary: 'Login Failed',
                        detail: error.message
                    });
                }
            });
    }

    loginWithGoogle() {
        this.isLoading = true;
        this.authService.loginWithGoogle(this.rememberMe)
            .subscribe({
                next: (user) => {
                    if (user) {
                        this.loginToBackend(user);
                    }
                },
                error: (error) => {
                    this.isLoading = false;
                    this.toasterService.add({
                        severity: 'error',
                        summary: 'Login Failed',
                        detail: error.message
                    });
                }
            });
    }

    get phoneOtpLabel(): string {
        return this.authService.usesWhatsAppOtp ? 'WhatsApp OTP' : 'Phone OTP';
    }

    get phoneOtpInstruction(): string {
        return this.authService.usesWhatsAppOtp
            ? 'Enter the verification code sent through WhatsApp to complete sign in.'
            : 'Enter the OTP sent to your phone to complete sign in.';
    }

    get sendOtpLabel(): string {
        return this.authService.usesWhatsAppOtp ? 'Send code' : 'Send OTP';
    }

    loginWithMicrosoft() {
        this.isLoading = true;
        this.authService.loginWithMicrosoft(this.rememberMe)
            .subscribe({
                next: (user) => {
                    if (user) {
                        this.loginToBackend(user);
                    }
                },
                error: (error) => {
                    this.isLoading = false;
                    this.toasterService.add({
                        severity: 'error',
                        summary: 'Login Failed',
                        detail: this.authErrorMessage(error)
                    });
                }
            });
    }

    forgotPassword(): void {
        if (!this.email.trim()) {
            this.toasterService.add({
                severity: 'warn',
                summary: 'Email Required',
                detail: 'Please enter your email address to reset your password.'
            });
            return;
        }

        this.isLoading = true;
        this.authService.requestPasswordReset(this.email).subscribe({
            next: () => {
                this.isLoading = false;
                this.toasterService.add({
                    severity: 'success',
                    summary: 'Check your email',
                    detail: 'If an account exists for that email, a password reset link will be sent shortly.'
                });
            },
            error: (error) => {
                this.isLoading = false;
                this.toasterService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: error.message
                });
            }
        });
    }

    loginWithPhone(): void {
        this.isPhoneLogin = true;
    }

    sendOtp(): void {
        if (!this.phoneNumber) {
            this.toasterService.add({ severity: 'warn', summary: 'Phone Number Required', detail: 'Please enter your phone number.' });
            return;
        }
        this.isLoading = true;
        this.authService.sendOtp(this.phoneNumber).subscribe({
            next: (confirmationResult) => {
                this.isLoading = false;
                this.confirmationResult = confirmationResult ?? {};
                this.toasterService.add({
                    severity: 'success',
                    summary: 'OTP Sent',
                    detail: this.authService.usesWhatsAppOtp
                        ? 'A verification code has been sent through WhatsApp.'
                        : 'An OTP has been sent to your phone.'
                });
            },
            error: (error) => {
                this.isLoading = false;
                this.toasterService.add({ severity: 'error', summary: 'Failed to Send OTP', detail: error.message });
            }
        });
    }

    verifyOtpAndLogin(): void {
        if (!this.otp) {
            this.toasterService.add({ severity: 'warn', summary: 'OTP Required', detail: 'Please enter the OTP you received.' });
            return;
        }
        this.isLoading = true;
        this.authService.verifyOtp(this.otp).subscribe({
            next: (user) => {
                if (user) {
                    this.loginToBackend(user);
                }
            },
            error: (error) => {
                this.isLoading = false;
                this.toasterService.add({ severity: 'error', summary: 'Login Failed', detail: error.message });
            }
        });
    }

    switchToEmailLogin(): void {
        this.isPhoneLogin = false;
        this.confirmationResult = null; // Reset confirmation result
    }

    private loginToBackend(user: User) {
        this.sessionStorageService.setRememberMe(this.rememberMe);
        this.authService.getCurrentUserAccessToken().subscribe({
            next: token => {
                this.sessionStorageService.saveAccessToken(token, new Date().getTime());
                this.sessionStorageService.resetBrowserSessionId();
                this.authService.activateCurrentBrowserSession().subscribe({
                    next: () => this.loadAuthenticatedWorkspace(user),
                    error: (error) => this.handleAuthenticatedLoginFailure(
                        error?.message || 'Unable to activate this browser session.'
                    )
                });
            },
            error: (error) => this.handleAuthenticatedLoginFailure(
                error?.message || 'Unable to complete sign in.'
            )
        });
    }

    private loadAuthenticatedWorkspace(user: User): void {
        this.customerService.getCustomerByFirebaseId(user.uid).subscribe(
            response => {
                let customer = response.data;
                this.isLoginFailed = false;
                this.isLoggedIn = true;
                this.sessionStorageService.saveUser(user);
                if (customer.company.transportationAgencyId) {
                    this.transportationAgencyService.findTransportationAgencyByFilter(undefined, undefined,
                        customer.company.transportationAgencyId, undefined, undefined).subscribe(agencyWrapperData => {
                            console.log(agencyWrapperData);
                            this.sessionStorageService.saveTransportationAgency(agencyWrapperData.data);
                        })
                }
                if (customer.company.freightForwarderId) {
                    this.freightForwarderService.getFreightForwarder(undefined, customer.company.freightForwarderId,
                        undefined, undefined, 'en-US').subscribe(freightForwarder => {
                            console.log(freightForwarder);
                            this.sessionStorageService.saveFreightForwarder(freightForwarder);
                        })
                }
                this.enterpriseFeatureService.getAllSubscriptions().subscribe({
                    next: (response) => {
                        console.log(response);
                    },
                    error: (error) => {
                        console.error('Error fetching enterprise features:', error);
                    }
                });
                this.roles = this.sessionStorageService.getUser().roles;
                forkJoin({
                    rolesData: this.customerService.getRolesByCustomerId(customer.id),
                    baseMasterData: this.baseMasterDataService.getBaseMasterData("en-US")
                }).subscribe({
                    next: ({ rolesData, baseMasterData }) => {
                        customer.roles = rolesData ?? [];
                        this.sessionStorageService.saveCustomer(customer);
                        console.log(baseMasterData);
                        this.sessionStorageService.saveBaseMasterData(baseMasterData.data);
                        this.isLoading = false;
                        this.toolbarService.updateLoggedInStatus(true);
                        this.toolbarService.updateCustomer(customer);
                        this.router.navigate([this.returnUrl == undefined || this.returnUrl == '/' ? '/dashboard' : this.returnUrl], { relativeTo: null });
                    },
                    error: (error) => {
                        this.isLoginFailed = true;
                        this.isLoggedIn = false;
                        this.isLoading = false;
                        this.toolbarService.updateLoggedInStatus(false);
                        this.toolbarService.updateCustomer(null);
                        this.toasterService.add({
                            severity: 'error',
                            summary: 'Login Failed',
                            detail: error?.message || 'Unable to load customer roles.'
                        });
                    }
                });
            },
            err => {
                this.handleAuthenticatedLoginFailure(
                    'No LogiLink user is linked to this sign-in account. Ask an administrator to link your account before signing in.'
                );
            }
        );
    }

    private handleAuthenticatedLoginFailure(detail: string): void {
        this.isLoginFailed = true;
        this.isLoggedIn = false;
        this.isLoading = false;
        this.sessionStorageService.signOut();
        this.toolbarService.updateLoggedInStatus(false);
        this.toolbarService.updateCustomer(null);
        this.authService.logout().subscribe({ error: () => undefined });
        this.toasterService.add({
            severity: 'error',
            summary: 'Login Failed',
            detail
        });
    }

    private authErrorMessage(error: any): string {
        if (error?.code === 'auth/account-exists-with-different-credential') {
            return 'An account already exists with this email using another sign-in method. Sign in with the original method first, then link Microsoft from your account.';
        }
        return error?.message || 'Unable to sign in.';
    }

    reloadPage(): void {
        window.location.reload();
    }

    findThirdOccurrence(str: string, char: string): number {
        let count = 0;
        let index = -1;

        for (let i = 0; i < str.length; i++) {
            if (str[i] === char) {
                count++;
                if (count === 3) {
                    index = i;
                    break;
                }
            }
        }

        return index;
    }

}
